/**
 * When the user returns to a reading route while audio is playing, scroll (or
 * switch surah) to the current ayah. Does not yank the user off About/Search/etc.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAudioContext } from '../contexts/AudioContext';
import { scrollAyahIntoViewSettled } from '../utils/scrollAyahIntoView';

function isReadingPath(pathname) {
  return (
    pathname.startsWith('/surah/') || pathname.startsWith('/arabicreading/')
  );
}

function MediaSessionAyahFocus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentlyPlayingAyah, chapterSnapshot, chapterAudio } = useAudioContext();
  const wasHiddenRef = useRef(false);
  const latestRef = useRef({});
  latestRef.current = {
    currentlyPlayingAyah,
    chapterSnapshot,
    chapterAudio,
    location,
    navigate,
  };

  useEffect(() => {
    const goToPlayingAyah = () => {
      const {
        currentlyPlayingAyah: playing,
        chapterSnapshot: snap,
        chapterAudio: audioCtrl,
        location: loc,
        navigate: nav,
      } = latestRef.current;

      // Only act on reading screens — never force-navigate from other routes
      if (!isReadingPath(loc.pathname)) return;

      const audioSnap = audioCtrl?.getSnapshot?.() || null;
      const isActivelyPlaying =
        (snap && !snap.isPaused && snap.mode) ||
        (audioSnap && !audioSnap.isPaused && audioSnap.mode);
      if (!isActivelyPlaying) return;

      const ayahKey =
        playing?.ayahKey ||
        snap?.ayahKey ||
        audioSnap?.ayahKey ||
        null;
      if (!ayahKey) return;

      const surahId = String(ayahKey).split(':')[0];
      if (!surahId) return;

      const onThisSurah =
        loc.pathname === `/surah/${surahId}` ||
        loc.pathname.startsWith(`/surah/${surahId}/`) ||
        loc.pathname === `/arabicreading/${surahId}`;

      if (onThisSurah) {
        scrollAyahIntoViewSettled(ayahKey, {
          instant: false,
          preferTopRatio: 0.22,
        });
        return;
      }

      // Already reading, but a different surah — move to the playing one
      nav(`/surah/${surahId}`, {
        state: {
          scrollToAyah: String(ayahKey),
          fromMediaSession: true,
          at: Date.now(),
        },
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState !== 'visible') return;
      if (!wasHiddenRef.current) return;
      wasHiddenRef.current = false;
      setTimeout(goToPlayingAyah, 80);
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return null;
}

export default MediaSessionAyahFocus;
